<script setup lang="tsx">
import { ref } from 'vue'
import http from '@/services/http'
import InstanceFilters from './InstanceFilters.vue'
import OrderLabel from '@/components/widgets/OrderLabel.vue'
import Pagination from '@/components/widgets/PaginationComponent.vue'
import useInstanceColumns from './columns'
import columnStore from '@/stores/column'
import type { Instance, PageRequest, PageResponse, Response } from '@/models/api-generated'
import LoadingContainer from '../widgets/LoadingContainer.vue'
import CustomHeader from '../custom_field/CustomHeader.vue'
import CustomRow from '../custom_field/CustomRow.vue'
import { fetchCustomValuesForPage, type CustomValueMap } from '@/services/custom'
import { instanceRefreshCallback } from './shared'
import sliderStore from '@/stores/slider'
import type { InstanceFilter } from '@/models/filters-generated'

const slider = sliderStore()
const model = defineModel<PageResponse<Instance>>()
const customValues = ref<CustomValueMap>()
const isLoading = ref(false)
const { columns, custom } = useInstanceColumns()
const root = columnStore().defineComponent('instances', fetch)
instanceRefreshCallback.value = root.triggerFetch

async function fetch(signal: AbortSignal) {
	const request: PageRequest<InstanceFilter> = {
		page: root.pagination.page,
		limit: 1000000,
		filter: root.filterValue() as InstanceFilter,
		orderBy: root.orderValue(),
	}

	isLoading.value = true
	const { data } = await http.post<Response<PageResponse<Instance>>>(`api/instance/list`, request, { signal })
	customValues.value = await fetchCustomValuesForPage('instances', data)
	isLoading.value = false

	if (data.data) model.value = data.data
}

function rowClick(instance: Instance) {
	slider.openInstance(instance.id, instance.name)
}
</script>

<template>
	<InstanceFilters></InstanceFilters>

	<LoadingContainer v-model="isLoading" class="rounded-container mt-3 p-3">
		<div class="table-responsive w-100">
			<table class="table">
				<thead>
					<tr>
						<th>
							{{ $t('mainInstitution') }}
						</th>
						<th v-if="columns.name.value.isVisible">
							<OrderLabel v-model="columns.name.value">{{ $t('misc.name') }}</OrderLabel>
						</th>
						<th v-if="columns.description.value.isVisible">
							<OrderLabel v-model="columns.description.value">{{ $t('description') }}</OrderLabel>
						</th>
						<th v-if="columns.address.value.isVisible">
							<OrderLabel v-model="columns.address.value">{{ $t('address') }}</OrderLabel>
						</th>
						<th v-if="columns.email.value.isVisible">
							<OrderLabel v-model="columns.email.value">{{ $t('email') }}</OrderLabel>
						</th>
						<th v-if="columns.phone.value.isVisible">
							<OrderLabel v-model="columns.phone.value">{{ $t('phone') }}</OrderLabel>
						</th>
						<th v-if="columns.contact.value.isVisible">
							<OrderLabel v-model="columns.contact.value">{{ $t('contact') }}</OrderLabel>
						</th>

						<CustomHeader :columns="custom"></CustomHeader>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="(instance, i) in model?.items"
						@mouseup.stop="rowClick(instance)"
						:key="instance.id"
						class="instance-row"
						:data-test="instance.id === 1 ? 'main-institution-yes' : 'main-institution-no'"
						:class="{ odd: i % 2 != 0 }"
					>
						<td>
							{{ instance.id > 1 ? $t('misc.no') : $t('misc.yes') }}
						</td>
						<td v-if="columns.name.value.isVisible">
							{{ instance.name }}
						</td>
						<td v-if="columns.description.value.isVisible">
							{{ instance.description }}
						</td>
						<td v-if="columns.address.value.isVisible">
							{{ instance.address }}
						</td>
						<td v-if="columns.email.value.isVisible">
							{{ instance.email }}
						</td>
						<td v-if="columns.phone.value.isVisible">
							{{ instance.phone }}
						</td>
						<td v-if="columns.contact.value.isVisible">
							{{ instance.contact }}
						</td>

						<CustomRow :columns="custom" v-model="customValues" :row-id="instance.id"></CustomRow>
					</tr>
				</tbody>
			</table>
		</div>
	</LoadingContainer>
	<Pagination v-model="root.pagination" :total-pages="model?.pages" :disableLimitSelect="true" />
</template>

<style lang="sass" scoped>
td:hover
    cursor: pointer
</style>
